import React from 'react';
import { HbrSpinner } from '@harbor/elements-react-standalone';

import { useSfPovActivityDetail } from '../../../hooks/useSfTeamPovActivitySummary';
import { useSfTeamOpportunitiesPovLean } from '../../../hooks/seSfTeamOpportunitiesPov';
import {
  povByProductRows,
  povOpportunitiesFromLean,
  povCoverageRows,
  povOppsByStageRows,
} from '../../../support/povActivityCharts';
import CampaignChartCard, { ChartGrid } from '../Campaigns/CampaignChartCard';

interface Props {
  email: string;
  timeFilter?: string;
  timeShort?: string;
  timePending?: boolean;
}

function Figure({ label, value }: { label: string; value: number | string }) {
  return (
    <div style={{ minWidth: 140 }}>
      <div className="hbr-css__text-secondary-sm">{label}</div>
      <div className="hbr-css__text-primary-lg-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}

/**
 * An individual's POV picture for the period: the three headline counts and charts of how their opportunities
 * split by POV coverage, stage and POC/POV SE activities. The opportunity list itself is the POV Opportunities table.
 */
export default function PovActivityByOpportunity({ email, timeFilter = 'all', timeShort, timePending = false }: Props) {
  const { data, isLoading, error } = useSfPovActivityDetail(email, timeFilter, { enabled: !!email && !timePending });

  const heading = timeShort && timeFilter !== 'all' ? `POV SE Activities by Opportunity — ${timeShort}` : 'POV SE Activities by Opportunity';
  const counts = data?.tree?.rollup;
  const opps = data?.opportunities ?? [];
  // The same POV Opportunities the table below lists (their POV product rows); the query is shared with it.
  const { data: leanData, isLoading: isLoadingLean } = useSfTeamOpportunitiesPovLean(email, {
    enabled: !!email && !timePending,
    currentTimeFilter: timeFilter,
  });
  const povByProduct = povByProductRows(povOpportunitiesFromLean(leanData));

  let body: React.ReactNode;
  if (timePending || isLoading) {
    body = <HbrSpinner>Loading POV activities…</HbrSpinner>;
  } else if (error || !data?.opportunities || !counts) {
    body = <div className="hbr-css__text-secondary-sm">POV activity data is unavailable right now.</div>;
  } else {
    body = (
      <>
        <div className="flex flex-wrap gap-6 mb-3">
          <Figure label="Opportunities" value={counts.opportunity_count} />
          <Figure label="Opps w/ POV Activity" value={counts.opps_with_pov_activity_count} />
          <Figure label="POV SE Activities" value={counts.pov_activity_count} />
        </div>
        {opps.length > 0 && (
          <div className="mb-3">
            <ChartGrid>
              <CampaignChartCard
                title="POV coverage"
                subtitle="Opportunities with and without a POV activity"
                chartType="donut_chart"
                data={povCoverageRows(counts)}
                tileKey={`pov-ic-coverage-${email}`}
                tooltipKeys={{ label: 'Opportunities', value: 'Count' }}
                empty="No opportunities close in this period."
              />
              <CampaignChartCard
                title="Opportunities by stage"
                subtitle={timeShort && timeFilter !== 'all' ? timeShort : 'All time'}
                chartType="donut_chart"
                data={povOppsByStageRows(opps)}
                tileKey={`pov-ic-stage-${email}`}
                tooltipKeys={{ label: 'Stage', value: 'Opportunities' }}
                empty="No opportunities close in this period."
              />
              <CampaignChartCard
                title="POV Activity by Product"
                subtitle="POVs on this person's opportunities, by product"
                chartType="donut_chart"
                data={isLoadingLean ? [] : povByProduct}
                tileKey={`pov-ic-by-product-${email}`}
                tooltipKeys={{ label: 'Product', value: 'POVs' }}
                empty={isLoadingLean ? 'Loading…' : 'No POVs on these opportunities.'}
              />
            </ChartGrid>
          </div>
        )}
        {!opps.length && <div className="hbr-css__text-secondary-sm">No open opportunities close in this period.</div>}
      </>
    );
  }

  return (
    <div className="hbr-css__container-card p-3 w-full">
      <div className="hbr-css__text-primary-sm-semibold mb-2">{heading}</div>
      {body}
    </div>
  );
}
